import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X, Pencil, Trash2, Plus, Mail, Phone, Globe, MapPin, Calendar, Percent, Building2, User, Clock, MessageSquare } from "lucide-react";
import { cn } from "@/lib/utils";
import ActivityFeed from "../dashboard/ActivityFeed";
import TasksList from "../tasks/TasksList";
import SentimentAnalysis from "../investors/SentimentAnalysis";
import DealStageTracker from "../investors/DealStageTracker";
import DocumentManager from "../investors/DocumentManager";
import HealthScoreDisplay from "../investors/HealthScoreDisplay";
import InvestmentsList from "../investors/InvestmentsList";
import { InvestmentHistory, ContactInformation, ConversationNotes } from "../investors/InvestorProfileSections";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Briefcase, User as UserIcon } from "lucide-react";

const statusColors = {
  lead: "bg-slate-100 text-slate-700",
  prospect: "bg-blue-100 text-blue-700",
  active: "bg-emerald-100 text-emerald-700",
  inactive: "bg-amber-100 text-amber-700",
  churned: "bg-rose-100 text-rose-700",
  in_discussion: "bg-blue-100 text-blue-700",
  committed: "bg-amber-100 text-amber-700",
  invested: "bg-emerald-100 text-emerald-700",
  exited: "bg-rose-100 text-rose-700",
};

const statusLabels = {
  lead: "Lead",
  prospect: "Prospekt",
  active: "Aktiv",
  inactive: "Inaktiv",
  churned: "Tapt",
  in_discussion: "I diskusjon",
  committed: "Forpliktet",
  invested: "Investert",
  exited: "Avsluttet",
};

const industryLabels = {
  technology: "Teknologi",
  healthcare: "Helse",
  finance: "Finans",
  retail: "Handel",
  manufacturing: "Produksjon",
  real_estate: "Eiendom",
  education: "Utdanning",
  consulting: "Konsultering",
  other: "Annet",
};

export default function DetailPanel({ 
  item, 
  type, 
  activities, 
  investments,
  tasks,
  onClose, 
  onEdit, 
  onDelete, 
  onAddActivity,
  onAddInvestment,
  onAddTask,
  onAddFollowUp,
  onEditTask,
  onDeleteTask,
  onTaskStatusChange,
  onUpdateDealStage,
  onUpdateValuation,
  isDeleting 
}) {
  if (!item) return null;

  const isCustomer = type === "customer";
  const name = isCustomer ? item.company_name : item.name;
  const subType = isCustomer ? item.industry : item.type;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-end">
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto animate-in slide-in-from-right duration-300">
        <div className="sticky top-0 bg-white z-10 border-b border-slate-100">
          <div className="flex items-center justify-between p-6">
            <div className="flex items-center gap-3">
              <div className={cn(
                "w-12 h-12 rounded-xl flex items-center justify-center",
                isCustomer ? "bg-gradient-to-br from-slate-100 to-slate-200" : "bg-gradient-to-br from-lime-100 to-lime-200"
              )}>
                {isCustomer ? (
                  <Building2 className="w-6 h-6 text-slate-600" />
                ) : (
                  <User className="w-6 h-6 text-zinc-900" />
                )}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">{name}</h2>
                <Badge className={cn("font-medium border-0 mt-1", statusColors[item.status])}>
                  {statusLabels[item.status] || item.status?.replace("_", " ")}
                </Badge>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* For Investors: Tabbed Detailed Sections */}
          {!isCustomer ? (
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="overview">Oversikt</TabsTrigger>
                <TabsTrigger value="investments" className="flex items-center gap-1">
                  <Briefcase className="w-3 h-3" />
                  Investeringer
                </TabsTrigger>
                <TabsTrigger value="conversations" className="flex items-center gap-1">
                  <MessageSquare className="w-3 h-3" />
                  Samtaler
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-6 mt-6">
                {/* Contact Information */}
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide">Kontaktinformasjon</h3>
                  <ContactInformation investor={item} />
                </div>

                {/* Notes */}
                {item.notes && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide">Notater</h3>
                    <p className="text-sm text-slate-600 leading-relaxed bg-slate-50 p-4 rounded-lg">{item.notes}</p>
                  </div>
                )}

                {/* Health Score */}
                <HealthScoreDisplay investor={item} activities={activities} tasks={tasks} />

                {/* Deal Stage Tracker */}
                {item.deal_stage && (
                  <DealStageTracker investor={item} onUpdateStage={onUpdateDealStage} />
                )}

                {/* Sentiment Analysis */}
                {activities && (
                  <SentimentAnalysis activities={activities} />
                )}

                {/* Document Manager */}
                <DocumentManager investor={item} activities={activities} />
              </TabsContent>

              <TabsContent value="investments" className="mt-6">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide">Investeringshistorikk</h3>
                    <Button variant="ghost" size="sm" onClick={onAddInvestment} className="text-zinc-900 hover:text-zinc-900">
                      <Plus className="w-4 h-4 mr-1" /> Ny Investering
                    </Button>
                  </div>
                  <InvestmentHistory investments={investments} onUpdateValuation={onUpdateValuation} />
                </div>
              </TabsContent>

              <TabsContent value="conversations" className="mt-6">
                <ConversationNotes activities={activities} />
              </TabsContent>
            </Tabs>
          ) : (
            /* For Customers: Keep Original Layout */
            <>
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide">Kontakt</h3>
                {item.contact_name && (
                  <div className="flex items-center gap-3 text-slate-700">
                    <User className="w-4 h-4 text-slate-400" />
                    <span>{item.contact_name}</span>
                  </div>
                )}
                {item.contact_email && (
                  <a href={`mailto:${item.contact_email}`} className="flex items-center gap-3 text-slate-700 hover:text-zinc-900">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span>{item.contact_email}</span>
                  </a>
                )}
                {item.contact_phone && (
                  <a href={`tel:${item.contact_phone}`} className="flex items-center gap-3 text-slate-700 hover:text-zinc-900">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span>{item.contact_phone}</span>
                  </a>
                )}
                {item.website && (
                  <a href={item.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-slate-700 hover:text-zinc-900">
                    <Globe className="w-4 h-4 text-slate-400" />
                    <span>{item.website}</span>
                  </a>
                )}
                {item.address && (
                  <div className="flex items-center gap-3 text-slate-700">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span>{item.address}</span>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide">Detaljer</h3>
                <div className="grid grid-cols-2 gap-4">
                  {subType && (
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Bransje</p>
                      <p className="text-sm font-medium text-slate-700">{industryLabels[subType] || subType?.replace("_", " ")}</p>
                    </div>
                  )}
                  {item.deal_value > 0 && (
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Avtaleverdi</p>
                      <p className="text-sm font-medium text-emerald-600">{item.deal_value.toLocaleString()} kr</p>
                    </div>
                  )}
                  {item.last_contact_date && (
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Siste kontakt</p>
                      <p className="text-sm font-medium text-slate-700">
                        {format(new Date(item.last_contact_date), "d. MMM yyyy")}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {item.notes && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide">Notater</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{item.notes}</p>
                </div>
              )}
            </>
          )}



          {/* Quick Actions for Investors */}
          {!isCustomer && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide">Hurtighandlinger</h3>
              <div className="grid grid-cols-2 gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full justify-start"
                  onClick={() => {
                    // This will be handled by the parent component
                    if (onAddFollowUp) onAddFollowUp();
                  }}
                >
                  <Clock className="w-4 h-4 mr-2" />
                  Planlegg oppfølging
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full justify-start"
                  onClick={onAddActivity}
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Logg samtale
                </Button>
              </div>
            </div>
          )}

          {/* Tasks */}
          {onAddTask && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide">Oppgaver</h3>
                <Button variant="ghost" size="sm" onClick={onAddTask} className="text-zinc-900 hover:text-zinc-900">
                  <Plus className="w-4 h-4 mr-1" /> Legg til Oppgave
                </Button>
              </div>
              {tasks && tasks.length > 0 ? (
                <TasksList 
                  tasks={tasks} 
                  onEdit={onEditTask}
                  onDelete={onDeleteTask}
                  onStatusChange={onTaskStatusChange}
                />
              ) : (
                <p className="text-sm text-slate-400 text-center py-4">Ingen oppgaver ennå</p>
              )}
            </div>
          )}

          {/* Activities */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide">Aktivitet</h3>
              <Button variant="ghost" size="sm" onClick={onAddActivity} className="text-zinc-900 hover:text-zinc-900">
                <Plus className="w-4 h-4 mr-1" /> Logg Aktivitet
              </Button>
            </div>
            <ActivityFeed activities={activities} />
          </div>
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-white border-t border-slate-100 p-4 flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onEdit}>
            <Pencil className="w-4 h-4 mr-2" /> Rediger
          </Button>
          <Button 
            variant="outline" 
            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" 
            onClick={onDelete}
            disabled={isDeleting}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}